import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { InputField } from "@/components/InputField/InputField";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetZohoDealConfigQuery,
	useSaveZohoDealConfigMutation,
	useGetZohoPipelinesQuery,
	useGetZohoOwnersQuery,
} from "@/pages/Private/redux/zoho/zoho.api";

import { ZohoDealConfig } from "../schema/zoho";

/** Treat "undefined", "null", and empty strings as null */
const sanitizeStr = (val: string | null | undefined): string | null => {
	if (!val || val === "undefined" || val === "null") {
		return null;
	}

	return val;
};

export const ZohoDealConfigTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.zoho.dealConfig.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: dealConfig, isLoading: isLoadingConfig } = useGetZohoDealConfigQuery();
	const { data: pipelinesData } = useGetZohoPipelinesQuery();
	const { data: ownersData } = useGetZohoOwnersQuery();
	const [saveDealConfig, { isLoading: isSaving }] = useSaveZohoDealConfigMutation();

	const [config, setConfig] = useState<ZohoDealConfig>({
		defaultPipeline: null,
		defaultDealStage: null,
		dealNameTemplate: "",
		closeDateOffset: 30,
		defaultOwner: null,
		autoCreateCampaigns: false,
	});

	useEffect(() => {
		if (dealConfig) {
			setConfig({
				defaultPipeline: sanitizeStr(dealConfig.defaultPipeline),
				defaultDealStage: sanitizeStr(dealConfig.defaultDealStage),
				dealNameTemplate:
					dealConfig.dealNameTemplate && dealConfig.dealNameTemplate !== "undefined"
						? dealConfig.dealNameTemplate
						: "",
				closeDateOffset: dealConfig.closeDateOffset ?? 30,
				defaultOwner: sanitizeStr(dealConfig.defaultOwner),
				autoCreateCampaigns: dealConfig.autoCreateCampaigns ?? false,
			});
		}
	}, [dealConfig]);

	const pipelineOptions: DropdownAutoCompleteItem[] = useMemo(
		() => pipelinesData?.pipelines?.map((p) => ({ id: p.id, title: p.label })) || [],
		[pipelinesData]
	);

	const stageOptions: DropdownAutoCompleteItem[] = useMemo(() => {
		if (!config.defaultPipeline) {
			return [];
		}

		const selectedPipeline = pipelinesData?.pipelines?.find((p) => p.id === config.defaultPipeline);

		return selectedPipeline?.stages?.map((s) => ({ id: s.id, title: s.label })) || [];
	}, [pipelinesData, config.defaultPipeline]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			ownersData?.owners?.map((o) => {
				const name = o.fullName ?? `${o.firstName || ""} ${o.lastName || ""}`.trim();

				return {
					id: o.id,
					title: name ? `${name} (${o.email})` : o.email,
				};
			}) || [],
		[ownersData]
	);

	const handleSave = async () => {
		try {
			const payload: ZohoDealConfig = {
				defaultPipeline: sanitizeStr(config.defaultPipeline),
				defaultDealStage: sanitizeStr(config.defaultDealStage),
				dealNameTemplate:
					config.dealNameTemplate && config.dealNameTemplate !== "undefined"
						? config.dealNameTemplate
						: null,
				closeDateOffset: config.closeDateOffset ?? null,
				defaultOwner: sanitizeStr(config.defaultOwner),
				autoCreateCampaigns: config.autoCreateCampaigns ?? false,
			};

			await saveDealConfig(payload).unwrap();
			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		}
	};

	if (isLoadingConfig) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex flex-col w-full gap-4">
			{renderMessageSnackbar()}
			<div className="text-sm text-gray-600 mb-1">{ts("description")}</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<Dropdown
					data={pipelineOptions}
					defaultValue={
						config.defaultPipeline
							? pipelineOptions.find((p) => p.id === config.defaultPipeline)
							: undefined
					}
					floating={true}
					handleSelect={(value) => {
						setConfig((prev) => ({
							...prev,
							defaultPipeline: value.id as string,
							defaultDealStage: null,
						}));
					}}
					label={ts("defaultPipeline")}
				/>

				<Dropdown
					key={config.defaultPipeline ?? "no-pipeline"}
					data={stageOptions}
					defaultValue={
						config.defaultDealStage
							? stageOptions.find((s) => s.id === config.defaultDealStage)
							: undefined
					}
					disabled={!config.defaultPipeline}
					floating={true}
					handleSelect={(value) => {
						setConfig((prev) => ({ ...prev, defaultDealStage: value.id as string }));
					}}
					label={ts("defaultStage")}
				/>

				<InputField
					handleChange={(e) => setConfig((prev) => ({ ...prev, dealNameTemplate: e.target.value }))}
					information={ts("dealNameTemplateHelp")}
					label={ts("dealNameTemplate")}
					name="dealNameTemplate"
					placeholder="{company} - {firstName} {lastName}"
					value={config.dealNameTemplate || ""}
				/>

				<InputField
					handleChange={(e) =>
						setConfig((prev) => ({
							...prev,
							closeDateOffset: parseInt(e.target.value, 10) || 0,
						}))
					}
					information={ts("closeDateOffsetHelp")}
					label={ts("closeDateOffsetDays")}
					min={0}
					name="closeDateOffset"
					type="number"
					value={config.closeDateOffset ?? 30}
				/>

				<Dropdown
					data={ownerOptions}
					defaultValue={
						config.defaultOwner
							? ownerOptions.find((o) => String(o.id) === String(config.defaultOwner))
							: undefined
					}
					floating={true}
					handleSelect={(value) => {
						setConfig((prev) => ({
							...prev,
							defaultOwner: value.id as string,
						}));
					}}
					information={ts("defaultOwnerHelp")}
					label={ts("defaultOwner")}
				/>
			</div>

			<div className="mt-4 pt-4 border-t border-gray-200">
				<Checkbox
					isChecked={config.autoCreateCampaigns ?? false}
					name="autoCreateCampaigns"
					onChange={() =>
						setConfig((prev) => ({
							...prev,
							autoCreateCampaigns: !prev.autoCreateCampaigns,
						}))
					}
				>
					{ts("autoCreateCampaigns")}
				</Checkbox>
				<div className="text-sm text-gray-600 mt-2">{ts("autoCreateCampaignsHelp")}</div>
			</div>

			<div className="flex justify-end mt-4">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					title={t("basics.save")}
					type="button"
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
